import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { useToast } from "@/hooks/use-toast";
import { siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { SiteSettings } from "@/types/content";

declare global {
  interface Window {
    turnstile?: {
      render: (
        container: HTMLElement,
        options: {
          sitekey: string;
          callback: (token: string) => void;
          "expired-callback": () => void;
          "error-callback": () => void;
          theme?: "light" | "dark";
        }
      ) => string;
      reset: (widgetId?: string) => void;
    };
  }
}

export default function ContactSection() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    subject: "",
    message: "",
  });
  const [turnstileToken, setTurnstileToken] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [turnstileReady, setTurnstileReady] = useState(false);
  const turnstileContainerRef = useRef<HTMLDivElement | null>(null);
  const turnstileWidgetId = useRef<string | null>(null);
  const siteKey = import.meta.env.VITE_TURNSTILE_SITE_KEY;

  const { toast } = useToast();
  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);

  useEffect(() => {
    if (!siteKey) return;

    const initializeTurnstile = () => {
      if (!window.turnstile || !turnstileContainerRef.current || turnstileWidgetId.current) return;
      turnstileWidgetId.current = window.turnstile.render(turnstileContainerRef.current, {
        sitekey: siteKey,
        theme: "dark",
        callback: token => setTurnstileToken(token),
        "expired-callback": () => setTurnstileToken(""),
        "error-callback": () => setTurnstileToken(""),
      });
      setTurnstileReady(true);
    };

    if (window.turnstile) {
      initializeTurnstile();
      return;
    }

    const existingScript = document.querySelector<HTMLScriptElement>(
      'script[src^="https://challenges.cloudflare.com/turnstile/v0/api.js"]'
    );
    if (existingScript) {
      existingScript.addEventListener("load", initializeTurnstile);
      return () => existingScript.removeEventListener("load", initializeTurnstile);
    }

    const script = document.createElement("script");
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.defer = true;
    script.addEventListener("load", initializeTurnstile);
    document.body.appendChild(script);

    return () => script.removeEventListener("load", initializeTurnstile);
  }, [siteKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (siteKey && !turnstileToken) {
      toast({
        title: "Verification needed",
        description: "Please complete the Turnstile challenge before submitting.",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSubmitting(true);
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ...formData,
          token: turnstileToken,
        }),
      });

      if (!response.ok) {
        const data = await response.json().catch(() => ({}));
        throw new Error(data?.message || "Unable to send message right now.");
      }

      toast({
        title: "Message sent",
        description: "Thanks for reaching out! I'll be in touch soon.",
      });

      setFormData({
        name: "",
        email: "",
        subject: "",
        message: "",
      });
      setTurnstileToken("");
      if (turnstileWidgetId.current && window.turnstile) {
        window.turnstile.reset(turnstileWidgetId.current);
      }
    } catch (error) {
      toast({
        title: "Message failed",
        description: error instanceof Error ? error.message : "Please try again in a moment.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section id="contact" className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-br from-card-bg to-dark-bg">
      <div className="max-w-4xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-mono text-pink-400">
            {settings?.contactHeading || "> ./contact.sh"}
          </h2>
          <p className="text-xl text-gray-300">
            {settings?.contactIntro || "Let's connect and build something secure together"}
          </p>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Contact Form */}
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-6 text-pink-400 font-mono">
              {settings?.contactFormHeading || "Send Message"}
            </h3>
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <Label htmlFor="name" className="block text-sm font-mono text-gray-300 mb-2">
                  Name
                </Label>
                <Input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  required
                  className="bg-dark-bg/50 border-gray-600 text-white placeholder-gray-400 focus:border-pink-400"
                  placeholder="Enter your name"
                />
              </div>
              <div>
                <Label htmlFor="email" className="block text-sm font-mono text-gray-300 mb-2">
                  Email
                </Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="bg-dark-bg/50 border-gray-600 text-white placeholder-gray-400 focus:border-pink-400"
                  placeholder="your@email.com"
                />
              </div>
              <div>
                <Label htmlFor="subject" className="block text-sm font-mono text-gray-300 mb-2">
                  Subject
                </Label>
                <Input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  required
                  className="bg-dark-bg/50 border-gray-600 text-white placeholder-gray-400 focus:border-pink-400"
                  placeholder="Project inquiry, collaboration, etc."
                />
              </div>
              <div>
                <Label htmlFor="message" className="block text-sm font-mono text-gray-300 mb-2">
                  Message
                </Label>
                <Textarea
                  id="message"
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  required
                  className="bg-dark-bg/50 border-gray-600 text-white placeholder-gray-400 focus:border-pink-400 h-32 resize-none"
                  placeholder="Tell me about your project or how we can work together..."
                />
              </div>
              {siteKey ? (
                <div className="space-y-2">
                  <div
                    ref={turnstileContainerRef}
                    className="flex justify-center"
                    aria-label="Cloudflare Turnstile"
                  />
                  {!turnstileReady && (
                    <p className="text-xs text-gray-400 font-mono text-center">
                      Loading verification challenge…
                    </p>
                  )}
                </div>
              ) : (
                <p className="text-xs text-gray-400 font-mono">
                  Turnstile is disabled until a site key is configured.
                </p>
              )}
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-pink-500 to-pink-400 text-white font-mono font-bold py-3 px-6 rounded-lg hover:scale-105 transition-transform duration-300 disabled:opacity-70 disabled:hover:scale-100"
                disabled={isSubmitting || (siteKey ? !turnstileToken : false)}
              >
                <span className="mr-2">&gt;</span>
                {isSubmitting ? "Sending..." : "Send Message"}
              </Button>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
